import { FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { NetworkSubmissionStepSelectRegulator } from "@app/compliance-connect/network-submission/NetworkSubmissionStepSelectRegulator";
import { NetworkSubmissionStepSelectProject } from "@app/compliance-connect/network-submission/NetworkSubmissionStepSelectProject";
import { NetworkSubmissionStepReview } from "@app/compliance-connect/network-submission/NetworkSubmissionStepReview";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { toast } from "react-toastify";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import {
  NetworkSubmissionContext,
  NetworkSubmissionContextProvider,
} from "@app/compliance-connect/network-submission/NetworkSubmissionContext";
import {
  NetworkSubmissionSummary,
  NetworkSubmissionWizardStep,
  NetworkSubmissionWizardStepName,
} from "@app/compliance-connect/network-submission/interfaces";
import { NetworkSubmissionWizardAction } from "@app/compliance-connect/network-submission/networkSubmissionWizardActions";
import { NetworkSubmissionStepAdequacyReview } from "@app/compliance-connect/network-submission/NetworkSubmissionStepAdequacyReview";
import { NetworkSubmissionStepExceptions } from "@app/compliance-connect/network-submission/NetworkSubmissionStepExceptions";


const HOME_LINK = "/network-submission";
const WIZARD_BASE = "/network-submission/create";

export const NetworkSubmissionWizard: FC = () => {
  return (
    <NetworkSubmissionContextProvider>
      <NetworkSubmissionWizardContent />
    </NetworkSubmissionContextProvider>
  );
};

const NetworkSubmissionWizardContent = (): JSX.Element => {
  const [summary, setSummary] = useState<NetworkSubmissionSummary | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const networkSubmissionService = useRef(new NetworkSubmissionService()).current;

  const wizardContext = useContext(NetworkSubmissionContext);
  const { dispatch } = wizardContext;
  
  const history = useHistory();
  const { step } = useParams<{ step?: string }>();

  useEffect(() => {
    const urlStep = step !== undefined ? parseInt(step, 10) : 0;
    const validStep: NetworkSubmissionWizardStep =
      !isNaN(urlStep) && urlStep >= NetworkSubmissionWizardStep.SelectRegulator && urlStep <= NetworkSubmissionWizardStep.Review
        ? urlStep
        : NetworkSubmissionWizardStep.SelectRegulator;
    dispatch({ payload: validStep, type: NetworkSubmissionWizardAction.SetPage });
  }, [step, dispatch]);

  const validateAndAdvance = () => {
    if (!wizardContext.isCurrentStepValid)
      return;

    handleUpdatePage(wizardContext.page + 1);
  };

  const handleUpdatePage = (newPage: number) => {
    history.push(`${WIZARD_BASE}/${newPage}`);
  };

  const currentWizardStep = useMemo(() => {
    switch (wizardContext.page) {
      case NetworkSubmissionWizardStep.SelectRegulator:
        return <NetworkSubmissionStepSelectRegulator />;
      case NetworkSubmissionWizardStep.SelectProject:
        return <NetworkSubmissionStepSelectProject />;
      case NetworkSubmissionWizardStep.AdequacyReview:
        return (
          <NetworkSubmissionStepAdequacyReview setSummary={setSummary} />
        );
      case NetworkSubmissionWizardStep.Exceptions:
        return <NetworkSubmissionStepExceptions setSummary={setSummary} />;
      case NetworkSubmissionWizardStep.Review:
        return <NetworkSubmissionStepReview />;
    }
  }, [wizardContext.page]);

  return (
    <Page documentTitle="Network Submission">
      <PageInner>
        <header className="page-title-bar row align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">Network Submission</h1>
        </header>
        <WizardSteps
          currentStep={wizardContext.page}
          steps={Object.values(NetworkSubmissionWizardStepName)}
          setStep={handleUpdatePage}
        />
        <hr />
        <PageSection>
          {currentWizardStep}
        </PageSection>
      </PageInner>

      <WizardNavigationBar
        leftSide={(
          <div className="d-flex align-items-center">
            <Link
              to={HOME_LINK}
              className="qes-button qes-button-secondary danger ms-4"
              data-testid="ns-wizard-step__cancel"
            >
              Cancel
            </Link>
            {summary && wizardContext.page !== NetworkSubmissionWizardStep.SelectRegulator && (
              <div data-testid="ns-bar__summary" className="d-flex align-items-center ms-4">
                Summary:
                <span data-testid="ns-bar__gaps-count" className="d-flex align-items-center ms-2">
                  <strong className="mx-1">{summary.totalGaps ?? 0}</strong>
                  <span>Gaps</span>
                </span>
                <span data-testid="ns-bar__missing-exceptions-count" className="d-flex align-items-center ms-2">
                  <strong className="mx-1">{summary.missingExceptions ?? 0}</strong>
                  <span>Missing Exception requests</span>
                </span>
              </div>
            )}
          </div>
        )}
        rightSide={(
          <div className="d-flex gap-2">
            {wizardContext.page !== NetworkSubmissionWizardStep.SelectRegulator && (
              <QESButton
                qesType="secondary"
                qesTestId="ns-wizard-step__previous"
                onClick={() => handleUpdatePage(wizardContext.page - 1)}
              >
                Previous
              </QESButton>
            )}

            {wizardContext.page === NetworkSubmissionWizardStep.Review ? (
              <QESButton
                qesType="primary"
                qesTestId="ns-wizard-step__submit"
                disabled={!wizardContext.isCurrentStepValid || isSubmitting}
                onClick={async () => {
                  setIsSubmitting(true);
                  try {
                    await networkSubmissionService.createSubmission({
                      comments: wizardContext.comments,
                      emailSharedUsers: wizardContext.emailSharedUsers,
                      pendingExceptions: [...wizardContext.pendingExceptions.values()],
                      projectId: wizardContext.selectedProject?.id,
                      regulatorClientId: wizardContext.regulatorClientId,
                      sharedUserIds: wizardContext.sharedUserIds,
                      submissionDescription: wizardContext.submissionDescription,
                      submissionName: wizardContext.submissionName,
                    });
                    toast.success("Network submission submitted successfully.", { containerId: "z-index-9999" });
                    history.push(HOME_LINK);
                  } catch {
                    toast.error("Failed to submit network submission. Please try again.", { containerId: "z-index-9999" });
                    setIsSubmitting(false);
                  }
                }}
              >
                Submit
              </QESButton>
            ) : (
              <QESButton
                className="pe-5 ps-5"
                qesType="primary"
                qesTestId="ns-wizard-step__next"
                disabled={!wizardContext.isCurrentStepValid}
                onClick={validateAndAdvance}
              >
                Next
              </QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
};
